import { StyleDc } from "@evergis/api";

import {
  StyleSymbolType,
  CompositeSymbolType,
  UnknownSymbol,
  isCompositeSymbol,
  isSimpleSymbol,
  isLabelSymbol,
  LabelType,
  RasterSymbol,
} from "../../../interfaces/style";
import { map } from "../../map";
import { Style } from "../../../ducks/style/style";

const { levels } = map.tileScheme;

export const createCompositeSymbol = (childSymbols: StyleSymbolType[]): CompositeSymbolType => ({
  type: "compositeSymbol",
  childSymbols,
});

export const getChildSymbols = (symbol?: UnknownSymbol): StyleSymbolType[] => {
  if (!symbol || !isCompositeSymbol(symbol)) return [];

  return symbol.childSymbols as StyleSymbolType[];
};

export const extractSymbol = (symbol?: UnknownSymbol): Pick<Style, "symbol" | "label" | "raster"> => {
  const childSymbols = getChildSymbols(symbol);

  return {
    symbol: childSymbols.find(isSimpleSymbol),
    label: (childSymbols.find(isLabelSymbol) as LabelType) || null,
    raster: (childSymbols.find(isCompositeSymbol) as RasterSymbol) || null,
  };
};

export const extractStyle = (style: StyleDc): Style => {
  if (style && isCompositeSymbol(style.symbol)) {
    return {
      ...style,
      ...extractSymbol(style.symbol),
      children: style.children?.map(extractStyle),
    };
  }

  // NOTE: for cases when style is exist style object should have same reference for all extractStyle calls
  return (style || {}) as Style;
};

export const packStyle = (style: Style, isInner = false): StyleDc => {
  const { ignoreLabel, label, symbol, raster, children, ...styleDc } = style || {};
  const childSymbols = [!ignoreLabel && label, raster].filter(Boolean);
  const minResolution = levels[0].resolution;
  const maxResolution = levels[levels.length - 1].resolution;

  return {
    ...styleDc,
    symbol: childSymbols.length ? createCompositeSymbol([symbol, ...(childSymbols as StyleSymbolType[])]) : symbol,
    children:
      children && !isInner
        ? children.map(childStyle =>
            packStyle(
              {
                ...childStyle,
                minResolution: childStyle.minResolution === minResolution ? 0 : childStyle.minResolution,
                maxResolution:
                  childStyle.maxResolution === maxResolution ? maxResolution + 1 : childStyle.maxResolution,
                ignoreLabel: childStyle.ignoreLabel || ignoreLabel,
                label: childStyle.label || label,
              },
              true,
            ),
          )
        : children,
  };
};
